import { createTheme } from '@mui/material';

import { error, info, neutrals, primary, secondary, Shadow, success, warning } from '../colors';
import { colors } from '@toolbox/style/theme/foundations/colors';

import type { TypographyOptions } from '@mui/material/styles/createTypography';

declare module '@mui/material' {
  interface PaletteColor {
    lighter?: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
  }
}

interface CommonFont {
  color: string;
  fontFamily: string;
}

const FrankFont: CommonFont = {
  color: primary.main,
  fontFamily: 'Frank Ruhl Libre',
};

const InterFont: CommonFont = {
  color: primary.main,
  fontFamily: 'Inter',
};

const typography: TypographyOptions = {
  h1: {
    ...FrankFont,
    fontSize: 48,
    fontWeight: 700,
    lineHeight: '56px',
  },
  h2: {
    ...FrankFont,
    fontSize: 32,
    fontWeight: 700,
    lineHeight: '40px',
  },
  h3: {
    ...InterFont,
    fontSize: 24,
    fontWeight: 700,
    lineHeight: '36px',
  },
  h4: {
    ...InterFont,
    fontSize: 20,
    fontWeight: 700,
    lineHeight: '30px',
  },
  h5: {
    ...InterFont,
    fontSize: 18,
    fontWeight: 700,
    lineHeight: '27px',
  },
  body1: {
    ...InterFont,
    fontSize: 16,
    fontWeight: 500,
    lineHeight: '24px',
  },
  body2: {
    ...InterFont,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '21px',
  },
  caption: {
    ...InterFont,
    fontSize: 12,
    fontWeight: 500,
    lineHeight: '18px',
    color: neutrals.dark,
  },
  button: {
    ...InterFont,
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '28px',
    textTransform: 'none',
  },
};

const originalTheme = createTheme();

const shadows = originalTheme.shadows;
shadows[1] = Shadow._100;
shadows[3] = Shadow._300;
shadows[5] = Shadow._500;
shadows[6] = Shadow._600;
shadows[10] = Shadow._1000;

export const defaultTheme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 500,
      md: 768,
      lg: 1024,
      xl: 1440,
    },
  },
  shape: {
    borderRadius: 5,
  },
  palette: {
    primary: {
      main: primary.main,
      light: primary.light,
      dark: primary.dark,
    },
    secondary: {
      main: secondary.main,
      light: secondary.light,
      lighter: secondary.lighter,
      dark: secondary.dark,
      contrastText: secondary.contrastText,
    },
    error: {
      main: error.main,
      light: error.light,
      dark: error.dark,
    },
    warning: {
      main: warning.main,
      light: warning.light,
    },
    success: {
      main: success.main,
      light: success.light,
      lighter: success.lighter,
    },
    info: {
      main: info.main,
      light: info.light,
    },
    divider: neutrals.lighter,
    common: {
      white: '#ffffff',
    },
  },
  typography: {
    ...typography,
  },
  shadows,
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minWidth: 260,
          color: primary.main,
          borderRadius: 0.25,
          '& fieldset': {
            borderColor: colors.gray['400'],
          },
          '&.active fieldset': {
            borderColor: primary.main,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: primary.main,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1px',
          },
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${neutrals.lighter}!important`,
          },
          '&.Mui-disabled': {
            background: neutrals.contrastText,
          },
          '&.Mui-focused svg': {
            color: primary.main,
          },
          '& svg': {
            color: neutrals.lighter,
          },
          '&.Mui-disabled svg.Mui-disabled': {
            color: neutrals.lighter,
          },
        },
        input: {
          '&.Mui-disabled': {
            WebkitTextFillColor: neutrals.lighter,
          },
        },
        notchedOutline: {
          border: `1px solid ${neutrals.lighter}`,
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: primary.main,
          '&.Mui-error': {
            color: error.dark,
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& p': {
            color: colors.gray['400'],
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          maxWidth: '80%',
          height: 'fit-content',
          color: primary.main,
          '&.Mui-focused': {
            whiteSpace: 'pre-wrap',
          },
          '&.MuiInputLabel-shrink': {
            color: primary.main,
          },
          '&.MuiFormLabel-filled': {
            color: primary.main,
          },
          '&.MuiFormLabel-filled + .MuiOutlinedInput-root .MuiInputAdornment-root p': {
            color: primary.main,
          },
          '&.MuiFormLabel-filled + .MuiOutlinedInput-root svg': {
            color: primary.main,
          },
          '&.MuiFormLabel-filled + .MuiOutlinedInput-root svg.Mui-disabled': {
            color: neutrals.lighter,
          },
          '&.Mui-disabled.MuiFormLabel-filled + .MuiOutlinedInput-root .MuiInputAdornment-root p': {
            color: neutrals.lighter,
          },
          '&.Mui-focused + .MuiOutlinedInput-root .MuiInputAdornment-root p': {
            color: primary.main,
          },
          '&.Mui-error': {
            color: error.dark,
          },
          '&.Mui-disabled': {
            color: neutrals.lighter,
          },
        },
        shrink: {
          maxWidth: '109%',
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        popper: {
          zIndex: '9999', // to fix for zindex in mccompass
        },
        listbox: {
          padding: originalTheme.spacing(1),
          '& li': {
            color: primary.main,
          },
          '& .MuiMenuItem-root:hover': {
            background: neutrals.contrastText,
          },
          '& .MuiMenuItem-root.Mui-selected': {
            background: success.lighter,
          },
          '& .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
            background: success.lighter,
          },
          '& .MuiMenuItem-root.Mui-focusVisible': {
            background: neutrals.contrastText,
          },
        },
        inputRoot: {
          padding: '8px',
          paddingRight: '8px !important',
          height: '56px',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: originalTheme.spacing(1),
          '& li': {
            color: primary.main,
          },
          '& .MuiMenuItem-root:hover': {
            background: neutrals.contrastText,
          },
          '& .MuiMenuItem-root.Mui-selected': {
            background: success.lighter,
          },
          '& .MuiMenuItem-root.Mui-selected.Mui-focusVisible': {
            background: success.lighter,
          },
          '& .MuiMenuItem-root.Mui-focusVisible': {
            background: neutrals.contrastText,
          },
        },
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        label: {
          fontSize: 16,
        },
      },
    },
    MuiStepContent: {
      styleOverrides: {
        root: {
          paddingLeft: 22,
          borderLeft: `3px solid ${primary.light}`,
        },
      },
    },
    MuiStepIcon: {
      styleOverrides: {
        root: {
          color: primary.light,
          '&.Mui-completed': {
            color: secondary.main,
            backgroundColor: primary.main,
            border: `3px solid ${primary.main}`,
            borderRadius: '50%',
          },
          '&.Mui-active': {
            color: secondary.main,
            border: `3px solid ${primary.main}`,
            borderRadius: '50%',
          },
        },
        text: {
          fill: 'none',
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        root: {
          color: primary.light,
          '&.Mui-completed': {
            color: primary.main,
          },
          '&.Mui-active': {
            color: primary.main,
          },
        },
        line: {
          borderColor: 'inherit',
        },
        lineVertical: {
          borderLeftWidth: 3,
        },
        vertical: {
          padding: 0,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: secondary.main,
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 40,
          '& svg': {
            color: primary.main,
            width: 24,
            height: 24,
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          '& span': {
            fontWeight: 500,
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        root: {
          width: 240,
          flexShrink: 0,
        },
        paper: {
          width: 240,
          boxSizing: 'border-box',
        },
      },
    },
    MuiModal: {
      styleOverrides: {
        root: {
          zIndex: '9999', // to fix for zindex in mccompass
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          '&:hover': {
            backgroundColor: primary.main,
            color: primary.contrastText,
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: primary.dark,
            color: primary.contrastText,
          },
        },
        sizeMedium: {
          fontSize: '16px',
          padding: '10px 16px',
        },
      },
    },
  },
});
